import { User, Mail, Phone } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { HostContact } from '@/types/api';

interface HostContactCardProps {
  hostContact?: HostContact | null;
}

/**
 * Displays host/owner contact information from the partner platform.
 * Returns null if no contact information is available.
 */
export function HostContactCard({ hostContact }: HostContactCardProps): JSX.Element | null {
  // Don't render if no contact info is available
  if (!hostContact) {
    return null;
  }

  const hasAnyInfo = hostContact.name ?? hostContact.email ?? hostContact.phone;
  if (!hasAnyInfo) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <User className="h-5 w-5" />
          Host Contact
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {hostContact.name && (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{hostContact.name}</span>
          </div>
        )}
        {hostContact.email && (
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <a
              href={`mailto:${hostContact.email}`}
              className="text-primary hover:underline"
            >
              {hostContact.email}
            </a>
          </div>
        )}
        {hostContact.phone && (
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <a
              href={`tel:${hostContact.phone}`}
              className="text-primary hover:underline"
            >
              {hostContact.phone}
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
